import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Input,
  Select,
  Pagination,
  PageButton
} from '../../styles/StyledComponents';
import { 
  FaPlus, 
  FaSearch, 
  FaFileExcel, 
  FaFilePdf, 
  FaEye, 
  FaEdit, 
  FaFilter, 
  FaSortAmountDown, 
  FaSortAmountUp, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaUserTie,
  FaChartLine,
  FaTimes,
  FaInfoCircle,
  FaCheckCircle,
  FaPause,
  FaProjectDiagram,
  FaArrowUp,
  FaArrowDown,
  FaDownload,
  FaLayerGroup
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { Project, ProjectStatus, ProjectType } from '../../types';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

// Type for sort fields including nested properties
type SortField = keyof Project | 'budget.total';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const PageContainer = styled.div`
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #141340;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .title-icon {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    box-shadow: 0 8px 24px rgba(246, 139, 33, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(246, 139, 33, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ color?: string; delay?: number }>`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out ${props => props.delay || 0}s both;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, ${props => props.color || '#F68B21'} 0%, ${props => props.color || '#e67c1a'} 100%);
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    
    .stat-icon {
      animation: ${pulse} 0.6s ease-in-out;
    }
  }
`;

const StatIcon = styled.div<{ bgColor?: string }>`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, ${props => props.bgColor || '#F68B21'} 0%, ${props => props.bgColor || '#e67c1a'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.3rem;
  box-shadow: 0 8px 24px ${props => props.bgColor || '#F68B21'}40;
  transition: all 0.3s ease;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #141340;
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
`;

const FiltersCard = styled(Card)`
  margin-bottom: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  animation: ${slideIn} 0.6s ease-out;
`;

const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f8f9fa;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: #141340;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 200px;
  
  @media (max-width: 768px) {
    min-width: 100%;
  }
  
  label {
    font-weight: 600;
    color: #141340;
    font-size: 0.9rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 2;
  
  @media (max-width: 768px) {
    flex: 1;
  }
  
  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-size: 1rem;
  }
  
  input {
    padding-left: 40px;
    border-radius: 12px;
    border: 2px solid #e9ecef;
    transition: all 0.3s ease;
    
    &:focus {
      border-color: #F68B21;
      box-shadow: 0 0 0 4px rgba(246, 139, 33, 0.1);
      transform: translateY(-1px);
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  
  @media (max-width: 576px) {
    flex-wrap: wrap;
  }
`;

const ExportButton = styled(OutlineButton)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 576px) {
    span {
      display: none;
    }
    padding: 0.75rem;
  }
`;

const FilterButton = styled(OutlineButton)<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  font-weight: 600;
  background-color: ${props => props.active ? 'rgba(246, 139, 33, 0.1)' : 'transparent'};
  border-color: ${props => props.active ? '#F68B21' : '#e9ecef'};
  color: ${props => props.active ? '#F68B21' : '#495057'};
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? 'rgba(246, 139, 33, 0.2)' : 'rgba(246, 139, 33, 0.05)'};
    border-color: #F68B21;
    color: #F68B21;
    transform: translateY(-1px);
  }
  
  @media (max-width: 576px) {
    padding: 0.75rem;
  }
`;

const ClearFiltersButton = styled(OutlineButton)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  border-color: #dc3545;
  color: #dc3545;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #dc3545;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
  }
  
  @media (max-width: 576px) {
    padding: 0.75rem;
  }
`;

const TableCard = styled(Card)`
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
`;

const TableActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  border: 1px solid #f1f3f4;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const ResultCount = styled.div`
  font-size: 0.95rem;
  color: #495057;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background: #F68B21;
    border-radius: 50%;
  }
`;

const SortButton = styled.button<{ active?: boolean }>`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.active ? '#F68B21' : '#6c757d'};
  font-weight: ${props => props.active ? '600' : '500'};
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(246, 139, 33, 0.1);
    color: #F68B21;
    transform: translateY(-1px);
  }
`;

const StatusBadge = styled(Badge)`
  padding: 0.35rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableActionButton = styled(Button)`
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(246, 139, 33, 0.3);
  }
`;

const ProjectMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #6c757d;
  
  svg {
    color: #F68B21;
  }
`;

const ProjectName = styled.div`
  font-weight: 700;
  color: #141340;
  font-size: 1rem;
  margin-bottom: 0.25rem;
`;

const ProjectId = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
  background: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  display: inline-block;
`;

const BudgetAmount = styled.div`
  font-weight: 700;
  color: #141340;
  font-size: 1rem;
`;

const BudgetStatus = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;
  
  .empty-icon {
    font-size: 4rem;
    color: #dee2e6;
    margin-bottom: 1.5rem;
  }
  
  .empty-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.75rem;
  }
  
  .empty-description {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const ProjectsList: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalProjects, setTotalProjects] = useState(0);
  const projectsPerPage = 10; // Show 10 projects per page
  
  // Load projects from API
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await apiService.getProjects({
          page: currentPage,
          limit: projectsPerPage,
          status: statusFilter || undefined,
          type: typeFilter || undefined,
          location: locationFilter || undefined
        });
        
        setProjects(response.data);
        setTotalProjects(response.pagination?.total || response.data.length);
      } catch (error: any) {
        console.error('Error loading projects:', error);
        setError('Failed to load projects');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user) {
      loadProjects();
    }
  }, [user, statusFilter, typeFilter, locationFilter, currentPage, projectsPerPage]);
  
  // Filter projects based on user role
  const getFilteredProjectsByUser = () => {
    if (user?.role === 'Project Manager') {
      return projects.filter(project => 
        project.assignedStaff?.some(staff => staff.userId === user.id)
      );
    }
    return projects;
  };
  
  const userProjects = getFilteredProjectsByUser();
  
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, typeFilter, locationFilter]);
  
  // Get unique values for filter dropdowns
  const projectTypes = [...new Set(userProjects.map((project: Project) => project.type))];
  const projectStatuses = [...new Set(userProjects.map((project: Project) => project.status))];
  const projectLocations = [...new Set(userProjects.map((project: Project) => project.location))];
  
  // Filter projects based on search and filters
  const filteredProjects = userProjects.filter((project: Project) => {
    const matchesSearch = searchTerm === '' || 
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === '' || project.status === statusFilter;
    const matchesType = typeFilter === '' || project.type === typeFilter;
    const matchesLocation = locationFilter === '' || project.location === locationFilter;
    
    return matchesSearch && matchesStatus && matchesType && matchesLocation;
  });
  
  // Sort projects
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (sortField === 'budget.total') {
      return sortDirection === 'asc' 
        ? a.budget.total - b.budget.total
        : b.budget.total - a.budget.total;
    }
    
    const aValue = a[sortField as keyof Project];
    const bValue = b[sortField as keyof Project];
    
    if (aValue === undefined || bValue === undefined) return 0;
    
    // Handle dates
    if (aValue instanceof Date && bValue instanceof Date) {
      return sortDirection === 'asc' 
        ? aValue.getTime() - bValue.getTime() 
        : bValue.getTime() - aValue.getTime();
    }
    
    // Handle strings
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    // Handle numbers
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });
  
  // Server-side pagination - API returns current page data
  const currentProjects = sortedProjects; // API already returns the correct page
  const totalPages = Math.ceil(totalProjects / projectsPerPage);
  const startIndex = (currentPage - 1) * projectsPerPage + 1;
  const endIndex = Math.min(currentPage * projectsPerPage, totalProjects);
  
  // Get status badge variant
  const getStatusVariant = (status: ProjectStatus) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In-Progress':
        return 'info';
      case 'Draft':
        return 'secondary';
      case 'On-Hold':
        return 'warning';
      case 'Cancelled':
        return 'danger';
      default:
        return 'primary';
    }
  };
  
  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setTypeFilter('');
    setLocationFilter('');
  };
  
  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };
  
  // Calculate project statistics based on filtered projects
  const getProjectCountByStatus = (status: string) => {
    return userProjects.filter(project => project.status === status).length;
  };
  
  const draftProjectsCount = getProjectCountByStatus('Draft');
  const inProgressProjectsCount = getProjectCountByStatus('In-Progress');
  const completedProjectsCount = getProjectCountByStatus('Completed');
  const onHoldProjectsCount = getProjectCountByStatus('On-Hold');
  
  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          <span className="title-icon">
            <IconWrapper icon={FaProjectDiagram} />
          </span>
          Projects Management
        </PageTitle>
        {user?.role !== 'Accountant' && (
          <Link to="/projects/new">
            <ActionButton>
              <IconWrapper icon={FaPlus} />
              New Project
            </ActionButton>
          </Link>
        )}
      </PageHeader>
      
      <StatsContainer>
        <StatCard color="#17a2b8" delay={0.1}>
          <StatIcon bgColor="#17a2b8" className="stat-icon">
            <IconWrapper icon={FaChartLine} />
          </StatIcon>
          <StatContent>
            <StatValue>{inProgressProjectsCount}</StatValue>
            <StatLabel>Active Projects</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard color="#ffc107" delay={0.2}>
          <StatIcon bgColor="#ffc107" className="stat-icon">
            <IconWrapper icon={FaEdit} />
          </StatIcon>
          <StatContent>
            <StatValue>{draftProjectsCount}</StatValue>
            <StatLabel>Draft Projects</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard color="#28a745" delay={0.3}>
          <StatIcon bgColor="#28a745" className="stat-icon">
            <IconWrapper icon={FaCheckCircle} />
          </StatIcon>
          <StatContent>
            <StatValue>{completedProjectsCount}</StatValue>
            <StatLabel>Completed Projects</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard color="#6c757d" delay={0.4}>
          <StatIcon bgColor="#6c757d" className="stat-icon">
            <IconWrapper icon={FaPause} />
          </StatIcon>
          <StatContent>
            <StatValue>{onHoldProjectsCount}</StatValue>
            <StatLabel>On Hold Projects</StatLabel>
          </StatContent>
        </StatCard>
      </StatsContainer>
      
      <FiltersCard>
        <CardBody>
          <FiltersHeader>
            <h3>
              <IconWrapper icon={FaSearch} />
              Search & Filter Projects
            </h3>
            <FilterButton 
              active={showFilters} 
              onClick={() => setShowFilters(!showFilters)}
            >
              <IconWrapper icon={FaFilter} />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </FilterButton>
          </FiltersHeader>
          
          <FiltersRow>
            <SearchContainer>
              <IconWrapper icon={FaSearch} className="search-icon" />
              <Input 
                type="text" 
                placeholder="Search by project name, ID, location..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchContainer>
            
            <ActionButtons>
              <ExportButton>
                <IconWrapper icon={FaFileExcel} />
                <span>Excel</span>
              </ExportButton>
              <ExportButton>
                <IconWrapper icon={FaFilePdf} />
                <span>PDF</span>
              </ExportButton>
            </ActionButtons>
          </FiltersRow>
            
          {showFilters && (
            <>
              <FiltersRow>
                <FilterGroup>
                  <label>Project Status:</label>
                  <Select 
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    {projectStatuses.map((status, index) => (
                      <option key={index} value={status}>{status}</option>
                    ))}
                  </Select>
                </FilterGroup>
                
                <FilterGroup>
                  <label>Project Type:</label>
                  <Select 
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <option value="">All Types</option>
                    {projectTypes.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </Select>
                </FilterGroup>
            
                <FilterGroup>
                  <label>Location:</label>
                  <Select 
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                  >
                    <option value="">All Locations</option>
                    {projectLocations.map((location, index) => (
                      <option key={index} value={location}>{location}</option>
                    ))}
                  </Select>
                </FilterGroup>
              </FiltersRow>
              
              <FiltersRow>
                <FlexContainer justify="flex-end">
                  <ClearFiltersButton onClick={clearFilters}>
                    <IconWrapper icon={FaTimes} />
                    Clear Filters
                  </ClearFiltersButton>
                </FlexContainer>
              </FiltersRow>
            </>
          )}
        </CardBody>
      </FiltersCard>
      
      <TableCard>
        <CardBody>
          <TableActionsRow>
            <ResultCount>
              <IconWrapper icon={FaLayerGroup} />
              Showing {startIndex}-{endIndex} of {totalProjects} projects
            </ResultCount>
            
            <FlexContainer gap="0.5rem">
              <span style={{ fontSize: '0.9rem', color: '#6c757d', fontWeight: '500' }}>Sort by:</span>
              <SortButton 
                active={sortField === 'name'} 
                onClick={() => handleSort('name')}
              >
                Name
                {sortField === 'name' && (
                  <IconWrapper icon={sortDirection === 'asc' ? FaArrowUp : FaArrowDown} />
                )}
              </SortButton>
              <SortButton 
                active={sortField === 'updatedAt'} 
                onClick={() => handleSort('updatedAt')}
              >
                Last Updated
                {sortField === 'updatedAt' && (
                  <IconWrapper icon={sortDirection === 'asc' ? FaArrowUp : FaArrowDown} />
                )}
              </SortButton>
              <SortButton 
                active={sortField === 'budget.total'} 
                onClick={() => handleSort('budget.total' as SortField)}
              >
                Budget
                {sortField === 'budget.total' && (
                  <IconWrapper icon={sortDirection === 'asc' ? FaArrowUp : FaArrowDown} />
                )}
              </SortButton>
            </FlexContainer>
          </TableActionsRow>
          
          <TableContainer>
            <Table>
              <TableHead>
                <tr>
                  <TableHeader>Project</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Budget</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </tr>
              </TableHead>
              <tbody>
                {currentProjects.length > 0 ? (
                  currentProjects.map((project) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <ProjectName>{project.name}</ProjectName>
                        <ProjectId>{project.id}</ProjectId>
                        <ProjectMeta>
                          <MetaItem>
                            <IconWrapper icon={FaUserTie} />
                            {project.assignedStaff?.map(staff => staff.name).join(', ')}
                          </MetaItem>
                          <MetaItem>
                            <span style={{ fontWeight: 600 }}>Project Manager:</span> {
                              project.assignedStaff?.find(staff => staff.role === 'Project Manager')?.name || 
                              project.manager || 
                              'Not assigned'
                            }
                          </MetaItem>
                          <MetaItem>
                            <IconWrapper icon={FaMapMarkerAlt} />
                            {project.location}
                          </MetaItem>
                          <MetaItem>
                            <IconWrapper icon={FaCalendarAlt} />
                            {new Date(project.updatedAt).toLocaleDateString()}
                          </MetaItem>
                        </ProjectMeta>
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant={getStatusVariant(project.status)}>
                          {project.status}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ fontWeight: '500', color: '#495057' }}>
                          {project.type}
                        </div>
                      </TableCell>
                      <TableCell>
                        <BudgetAmount>
                          {formatCurrency(project.budget.total, project.budget.currency)}
                        </BudgetAmount>
                        <BudgetStatus>
                          <IconWrapper icon={project.budget.approved ? FaCheckCircle : FaInfoCircle} />
                          {project.budget.approved ? 'Approved' : 'Pending Approval'}
                        </BudgetStatus>
                      </TableCell>
                      <TableCell>
                        <FlexContainer gap="0.5rem">
                          <Link to={`/projects/${project._id || project.id}`}>
                            <TableActionButton>
                              <IconWrapper icon={FaEye} />
                              View
                            </TableActionButton>
                          </Link>
                          <Link to={`/projects/edit/${project._id || project.id}`}>
                            <TableActionButton>
                              <IconWrapper icon={FaEdit} />
                              Edit
                            </TableActionButton>
                          </Link>
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <EmptyState>
                        <div className="empty-icon">
                          <IconWrapper icon={FaProjectDiagram} />
                        </div>
                        <div className="empty-title">No projects found</div>
                        <div className="empty-description">
                          Try adjusting your search or filter criteria, or create a new project to get started.
                        </div>
                      </EmptyState>
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>
          </TableContainer>
          
          {totalPages > 1 && (
            <Pagination>
              <PageButton 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </PageButton>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PageButton 
                  key={page} 
                  active={currentPage === page}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </PageButton>
              ))}
              
              <PageButton 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </PageButton>
            </Pagination>
          )}
        </CardBody>
      </TableCard>
      
      <div style={{ 
        textAlign: 'right', 
        padding: '2rem 1rem 1rem', 
        color: '#6c757d', 
        fontSize: '0.8rem' 
      }}>
        Developed by Sandfish
      </div>
    </PageContainer>
  );
};

export default ProjectsList; 